import { type ComponentProps, Fragment, Suspense } from 'react';
import { Card } from '@startuptools/ui/Card';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Table } from '@startuptools/ui/Table';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import { type GqlCompany } from '../../../../graphql/base-types.graphql';
import { type GqlFullReportTableQuery } from './FullReportTable.graphql';
import { RichUserText } from '@startuptools/ui/RichUserText';
import { Identity } from '@startuptools/business-ui/identity/Identity';

const FormatBool = ({ children }: { inSwedish: boolean; children: boolean }) => {
  return children ? <Trans id="Yes" /> : <Trans id="No" />;
};

const FullReportTableInner = ({
  rows,
  company,
}: {
  rows: GqlFullReportTableQuery['fullShareReg'];
  company: Pick<
    GqlCompany,
    'reservationPostTransferPurchaseRight' | 'reservationRightOfFirstRefusal' | 'reservationConsent'
  >;
}) => {
  const { i18n } = useLingui();
  const inSwedish = i18n.locale === 'sv';

  const showAvatar = rows.length <= 50;

  return (
    <Card sx={{ overflowX: 'auto' }}>
      <Table sx={{ tableLayout: 'auto' }}>
        <thead>
          <tr>
            <th>
              <Trans id="Share Block" />
            </th>
            <th>
              <Trans id="Share Numbers" />
            </th>
            <th align="right">
              <Trans id="Number of Shares" />
            </th>
            <th>
              <Trans id="Share Class" />
            </th>
            <th>
              <Trans id="Notes" />
            </th>
            <th>
              <Trans id="Registration Date" />
            </th>
            <th>
              <Trans id="Shareholder" />
            </th>
            <th>
              <Trans id="Address" />
            </th>
            <th>
              <Typography style={{ writingMode: 'vertical-lr', transform: 'rotate(-180deg)' }}>
                <Trans id="Has Share Certificate" />
              </Typography>
            </th>
            <th>
              <Typography style={{ writingMode: 'vertical-lr', transform: 'rotate(-180deg)' }}>
                <Trans id="Has Guardian" />
              </Typography>
            </th>
            <th>
              <Typography style={{ writingMode: 'vertical-lr', transform: 'rotate(-180deg)' }}>
                <Trans id="Post sale purchase right" />
              </Typography>
            </th>
            <th>
              <Typography style={{ writingMode: 'vertical-lr', transform: 'rotate(-180deg)' }}>
                <Trans id="Right of first refusal" />
              </Typography>
            </th>
            <th>
              <Typography style={{ writingMode: 'vertical-lr', transform: 'rotate(-180deg)' }}>
                <Trans id="Board approval required" />
              </Typography>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <Fragment key={i}>
              <tr style={{ textDecoration: row.strike ? 'line-through' : undefined }}>
                <td>{row.blockName}</td>
                <td>
                  <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                    {row.blockStart}
                  </NumberFormat>
                  {'-'}
                  <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                    {row.blockEnd}
                  </NumberFormat>
                </td>
                <td align="right">
                  <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                    {row.numberOfShares}
                  </NumberFormat>
                </td>
                <td>{row.shareClassName}</td>
                <td>{i18n.locale === 'sv' ? row.note.primaryLang : row.note.secondaryLang}</td>
                <td>
                  <DateFormat locale={i18n.locale}>{row.registrationDate}</DateFormat>
                </td>
                <td>
                  <Identity identity={row.shareholderIdentity} showAvatar={showAvatar} />
                </td>
                <td>{row.address}</td>
                <td>{row.hasShareCertificate ? <Trans id="Yes" /> : <Trans id="No" />}</td>
                <td>{row.hasGuardian ? <Trans id="Yes" /> : <Trans id="No" />}</td>
                <td>
                  <FormatBool inSwedish={inSwedish}>{company.reservationPostTransferPurchaseRight}</FormatBool>
                </td>
                <td>
                  <FormatBool inSwedish={inSwedish}>{company.reservationRightOfFirstRefusal}</FormatBool>
                </td>
                <td>
                  <FormatBool inSwedish={inSwedish}>{company.reservationConsent}</FormatBool>
                </td>
              </tr>
              {row.comment && (
                <tr>
                  <td />
                  <td colSpan={11}>
                    <RichUserText>{row.comment}</RichUserText>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </Table>
    </Card>
  );
};

export const FullReportTable = (props: ComponentProps<typeof FullReportTableInner>) => {
  return (
    <Suspense>
      <FullReportTableInner {...props} />
    </Suspense>
  );
};
